import type { Y } from '@piccolohealth/pbs-common';
import { ChakraV3, ScrollArea } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import Collaboration from '@tiptap/extension-collaboration';
import CollaborationCursor from '@tiptap/extension-collaboration-cursor';
import type { Extensions, Editor as TiptapEditor } from '@tiptap/react';
import React from 'react';
import { Editor } from '../../components/tiptap/Editor';
import { useEditor } from '../../components/tiptap/hooks/useEditor';
import {
  type HocuspocusState,
  useHocuspocusProvider,
} from '../../components/tiptap/hooks/useHocuspocusProvider';
import { EditorLayout } from '../../components/tiptap/layout/EditorLayout';
import { EditorMenu } from '../../components/tiptap/menus/EditorMenu';
import { TableOfContentView } from '../../components/tiptap/TableOfContentView';

interface Props {
  documentId: string;
  extensions: Extensions;
  isEditable: boolean;
  title?: (options: {
    editor: TiptapEditor;
    state: HocuspocusState;
  }) => React.ReactNode;
  toolbarContent?: (editor: TiptapEditor) => React.ReactNode;
  mainContent?: (editor: TiptapEditor, yDoc: Y.Doc) => React.ReactNode;
  sidebarContent?: (editor: TiptapEditor, yDoc: Y.Doc) => React.ReactNode | null;
}

export const TemplateEditor = (props: Props) => {
  const { title, documentId, extensions, isEditable, toolbarContent, mainContent, sidebarContent } =
    props;

  const { provider, user, state, yDoc } = useHocuspocusProvider({
    documentId,
  });

  const { editor } = useEditor(
    {
      isEditable,
      extensions: P.compact([
        ...extensions,
        Collaboration.configure({
          document: provider.document,
        }),
        // Read only editors we don't want to have any "awareness" in the backend.
        // I.e. they are viewing this in read only mode.
        isEditable
          ? CollaborationCursor.configure({
              provider,
              user,
            })
          : null,
      ]),
    },
    [isEditable],
  );

  if (!editor) {
    return null;
  }

  const computedSidebarContent = P.run(() => {
    if (P.isUndefined(sidebarContent)) {
      return (
        <>
          <ChakraV3.Text px={3} pt={2} fontWeight='semibold'>
            Outline
          </ChakraV3.Text>
          <ScrollArea h='full' overflowY='auto'>
            <TableOfContentView editor={editor} />
          </ScrollArea>
        </>
      );
    }
    return sidebarContent(editor, yDoc);
  });

  const computedToolbarContent = P.run(() => {
    if (!isEditable) {
      return null;
    }

    if (P.isUndefined(toolbarContent)) {
      return <EditorMenu editor={editor} />;
    }

    return toolbarContent(editor);
  });

  return (
    <EditorLayout
      documentId={documentId}
      state={state}
      isSidebarOpen={!P.isNull(computedSidebarContent)}
      headerContent={title?.({ editor, state })}
      mainContent={mainContent ? mainContent(editor, yDoc) : <Editor editor={editor} />}
      toolbarContent={computedToolbarContent}
      sidebarContent={computedSidebarContent}
    />
  );
};
